import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useKnowledgeBaseArticles,
  KnowledgeBaseArticle,
} from '../../knowledge-base/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentKnowledgeBaseArticlesProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentKnowledgeBaseArticles: React.FC<RecentKnowledgeBaseArticlesProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: articles, isLoading } = useKnowledgeBaseArticles();

  const items = React.useMemo(() => {
    const arr = Array.isArray(articles) ? articles : [];
    return [...arr]
      .filter((a: KnowledgeBaseArticle) => a.status === 'published')
      .sort((a: KnowledgeBaseArticle, b: KnowledgeBaseArticle) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [articles, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<KnowledgeBaseArticle>
      title="Recent KB Articles"
      viewAllPath="/knowledge-base"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No recent articles"
      getItemKey={(a) => a.id}
      embedded={embedded}
      renderItem={(article) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/knowledge-base/${article.id}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/knowledge-base/${article.id}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {article.title}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {article.category?.name && `${article.category.name} · `}
              {article.updatedAt
                ? new Date(article.updatedAt).toLocaleDateString('en-US')
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentKnowledgeBaseArticles;
